import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Flame, RefreshCw, Users, Trophy, ExternalLink, Palette, Copy, FileText, Pencil, Code2, Merge } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { money, ShareControls, brandOf } from "@/components/fusion/FusionShared";
import { useFusionAccess } from "@/lib/useFusionAccess";
import TeamCheckoutDialog from "@/components/TeamCheckoutDialog";

export default function CoachFusion() {
  const [events, setEvents] = useState(null);
  const [dash, setDash] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [resultsFor, setResultsFor] = useState(null);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [emailInviteOpen, setEmailInviteOpen] = useState(false);
  const access = useFusionAccess();

  const loadEvents = async () => {
    const { data } = await api.get("/fusion/events/mine");
    setEvents(data.events);
    if (data.events.length && !selected) setSelected(data.events[0].event_id);
  };
  const loadDash = async (eid) => {
    const { data } = await api.get(`/fusion/events/${eid}/dashboard`);
    setDash(data);
  };
  useEffect(() => { loadEvents(); }, []); // eslint-disable-line
  useEffect(() => { if (selected) loadDash(selected); }, [selected]); // eslint-disable-line

  const activate = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/fusion/events/${selected}/activate-athletes`);
      toast.success(`${data.created} athlete campaign(s) created (${data.total_campaigns} total)`);
      loadDash(selected);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const mergeDuplicates = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/fusion/events/${selected}/merge-duplicates`);
      toast.success(`Merged ${data.merged} duplicate page(s) — ${data.total_campaigns} athletes now`);
      loadDash(selected);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  const refreshPayments = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/fusion/events/${selected}/refresh-payments`);
      toast.success(`Checked ${data.checked} invoices — ${data.newly_paid} newly paid`);
      loadDash(selected);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };

  if (!events) return <div className="text-white/40 p-8 animate-pulse">Loading Fusion…</div>;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="coach-fusion-page">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-cyan-400">BioHacker Fusion™</div>
          <h1 className="text-2xl font-black text-white mt-1">Fusion Fundraising</h1>
          <p className="text-sm text-white/50">Performance combine + Hit-A-Thon fundraiser. Raise. Measure. Develop. Repeat.™</p>
        </div>
        <div className="flex gap-2">
          {events.map((e) => (
            <button key={e.event_id} onClick={() => setSelected(e.event_id)} data-testid={`event-tab-${e.event_id}`}
              className={`px-3 py-1.5 rounded-full text-xs font-bold border ${selected === e.event_id ? "bg-cyan-400 text-[#0b1424] border-cyan-400" : "text-white/60 border-white/15"}`}>
              {e.name}
            </button>
          ))}
          <CreateEventDialog onCreated={(ev) => { loadEvents().then(() => setSelected(ev.event_id)); }} />
        </div>
      </header>

      {events.length === 0 && (
        <div className="rounded-2xl border border-dashed border-white/15 p-12 text-center text-white/50" data-testid="fusion-empty-state">
          No Fusion event yet. Create one to auto-generate your team campaign page and every athlete's personal fundraiser page.
        </div>
      )}

      {dash && (
        <>
          {/* Totals strip */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            {[
              ["Raised", money(dash.totals.raised_usd)],
              ["Team goal", money(dash.totals.team_goal_usd)],
              ["% to goal", `${dash.totals.pct_to_goal}%`],
              ["Athletes", dash.totals.athlete_count],
              ["Sponsors", dash.totals.sponsor_count],
            ].map(([l, v]) => (
              <div key={l} className="rounded-xl border border-white/10 bg-white/5 p-4">
                <div className="text-[10px] uppercase tracking-widest text-white/40">{l}</div>
                <div className="text-xl font-black text-white mt-1" data-testid={`fusion-stat-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{v}</div>
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex flex-wrap gap-2 items-center">
            <Button onClick={activate} disabled={busy} className="bg-cyan-400 text-[#0b1424] font-bold hover:bg-cyan-300" data-testid="activate-athletes-btn">
              <Users className="h-4 w-4 mr-1.5" /> Activate roster athletes
            </Button>
            <Button onClick={refreshPayments} disabled={busy} variant="outline" className="border-white/20 text-white" data-testid="refresh-payments-btn">
              <RefreshCw className="h-4 w-4 mr-1.5" /> Refresh payment statuses
            </Button>
            <EditEventDialog event={dash.event} onSaved={() => { loadDash(selected); loadEvents(); }} />
            <BrandingDialog event={dash.event} onSaved={() => loadDash(selected)} />
            <EmbedDialog slug={dash.event.slug} />
            <a href={`/fusion/team/${dash.event.slug}`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1.5 text-xs font-bold text-cyan-400 hover:underline" data-testid="view-team-page-link">
              <ExternalLink className="h-3.5 w-3.5" /> View public team page
            </a>
          </div>

          <ShareControls url={`${window.location.origin}/fusion/team/${dash.event.slug}`}
            title={`Support ${brandOf(dash.event).school} — BioHacker Fusion Combine & Hit-A-Thon`}
            color={brandOf(dash.event).secondary} compact />

          {/* Unlock full BioHacker — shown to Fusion-only teams */}
          {access.loaded && !access.biohacker_enabled && (
            <section className="rounded-2xl border border-fuchsia-400/25 bg-fuchsia-400/[0.06] p-5" data-testid="unlock-biohacker-card">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="max-w-xl">
                  <div className="font-black text-white text-sm">Unlock the full BioHacker platform</div>
                  <div className="text-xs text-white/55 mt-0.5">
                    Your Fusion combine results already feed permanent athlete profiles. The Team plan adds daily readiness,
                    workload & injury-risk monitoring, verified test history, 2D/3D biomechanics, and the full coach console —
                    <b className="text-white"> $1,495/yr for up to 40 athletes</b>.
                  </div>
                </div>
                <Button onClick={() => setUpgradeOpen(true)} className="bg-fuchsia-400 text-[#0b1424] font-black hover:bg-fuchsia-300" data-testid="unlock-biohacker-btn">
                  Unlock full BioHacker
                </Button>
              </div>
            </section>
          )}

          {/* Athlete invite link */}
          <section className="rounded-2xl border border-cyan-400/25 bg-cyan-400/[0.06] p-5" data-testid="athlete-invite-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="font-black text-white text-sm">Invite your athletes</div>
                <div className="text-xs text-white/55 mt-0.5">
                  Send this link to players — they create their Fusion account and instantly get their own fundraising page on your team campaign.
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button size="sm" className="bg-cyan-400 text-[#0b1424] font-bold hover:bg-cyan-300"
                  onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/fusion/join/${dash.invite_code}`).then(() => toast.success("Athlete invite link copied")); }}
                  data-testid="copy-invite-link-btn">
                  <Copy className="h-4 w-4 mr-1.5" /> Copy athlete link
                </Button>
                <a className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold border border-white/15 bg-white/5 text-white hover:bg-white/10"
                  href={`sms:?&body=${encodeURIComponent(`Join our ${dash.event.name}! Create your fundraising account here: ${window.location.origin}/fusion/join/${dash.invite_code}`)}`}
                  data-testid="sms-invite-link-btn">Text it</a>
                <a className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-xs font-bold border border-white/15 bg-white/5 text-white hover:bg-white/10"
                  href={`mailto:?subject=${encodeURIComponent(`Join the ${dash.event.name}`)}&body=${encodeURIComponent(`Create your Fusion fundraising account: ${window.location.origin}/fusion/join/${dash.invite_code}`)}`}
                  data-testid="email-invite-link-btn">Email it</a>
                <Button size="sm" variant="outline" className="border-cyan-400/40 text-cyan-300 bg-transparent hover:bg-cyan-400/10"
                  onClick={() => setEmailInviteOpen(true)} data-testid="bulk-email-invite-btn">
                  Email the whole roster
                </Button>
              </div>
            </div>
            <div className="mt-2 text-[11px] font-mono text-white/40 break-all" data-testid="invite-link-text">
              {window.location.origin}/fusion/join/{dash.invite_code}
            </div>
          </section>

          {dash.duplicate_groups?.length > 0 && (
            <section className="rounded-2xl border border-amber-400/30 bg-amber-400/[0.07] p-5" data-testid="duplicates-banner">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <div className="font-black text-white text-sm">{dash.duplicate_groups.length} athlete{dash.duplicate_groups.length > 1 ? "s appear" : " appears"} more than once</div>
                  <div className="text-xs text-white/60 mt-0.5">
                    {dash.duplicate_groups.map((g) => `${g[0].athlete_name} (×${g.length})`).join(" · ")}
                  </div>
                  <div className="text-[11px] text-white/45 mt-1">Merging keeps one page per athlete, combines their pledges and totals, and keeps every login working.</div>
                </div>
                <Button onClick={mergeDuplicates} disabled={busy} className="bg-amber-400 text-[#0b1424] font-bold hover:bg-amber-300" data-testid="merge-duplicates-btn">
                  <Merge className="h-4 w-4 mr-1.5" /> Merge duplicates
                </Button>
              </div>
            </section>
          )}

          {/* Athlete campaigns */}
          <section>
            <h2 className="text-base font-black text-white mb-3">Athlete campaigns</h2>
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-sm" data-testid="fusion-campaigns-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 bg-white/[0.03]">
                    <th className="px-4 py-3">Athlete</th><th className="px-4 py-3">Raised</th>
                    <th className="px-4 py-3">Sponsors</th><th className="px-4 py-3">Best hit</th>
                    <th className="px-4 py-3">Result</th><th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {dash.campaigns.map((c) => (
                    <tr key={c.campaign_id} className="border-t border-white/5 text-white/80">
                      <td className="px-4 py-3 font-bold text-white">{c.athlete_name}</td>
                      <td className="px-4 py-3">{money(c.raised_usd)} <span className="text-white/40">/ {money(c.goal_usd)}</span></td>
                      <td className="px-4 py-3">{c.sponsor_count}</td>
                      <td className="px-4 py-3">{c.best_distance_ft ? `${c.best_distance_ft.toFixed(0)} ft` : "—"}</td>
                      <td className="px-4 py-3">
                        {c.result_verified
                          ? <span className="text-emerald-400 font-bold text-xs" data-testid={`verified-badge-${c.campaign_id}`}>VERIFIED</span>
                          : <span className="text-white/40 text-xs">pending</span>}
                      </td>
                      <td className="px-4 py-3 text-right space-x-2 whitespace-nowrap">
                        <a href={`/fusion/a/${c.slug}`} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 hover:underline" data-testid={`view-campaign-${c.campaign_id}`}>Page</a>
                        <button onClick={() => setResultsFor(c)} className="text-xs font-bold text-amber-400 hover:underline" data-testid={`enter-results-${c.campaign_id}`}>
                          {c.result_verified ? "Edit swings" : "Enter 5 swings"}
                        </button>
                      </td>
                    </tr>
                  ))}
                  {dash.campaigns.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">No athlete campaigns yet — click "Activate roster athletes".</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Pledges */}
          <section>
            <h2 className="text-base font-black text-white mb-3">Sponsor pledges & payments</h2>
            <div className="overflow-x-auto rounded-xl border border-white/10">
              <table className="w-full text-sm" data-testid="fusion-pledges-table">
                <thead>
                  <tr className="text-left text-[10px] uppercase tracking-widest text-white/40 bg-white/[0.03]">
                    <th className="px-4 py-3">Sponsor</th><th className="px-4 py-3">Email</th>
                    <th className="px-4 py-3">Type</th><th className="px-4 py-3">Amount</th>
                    <th className="px-4 py-3">Status</th><th className="px-4 py-3">Invoice</th>
                  </tr>
                </thead>
                <tbody>
                  {dash.pledges.map((p) => (
                    <tr key={p.pledge_id} className="border-t border-white/5 text-white/80">
                      <td className="px-4 py-3 font-semibold text-white">{p.sponsor_first} {p.sponsor_last}</td>
                      <td className="px-4 py-3 text-xs">{p.sponsor_email}</td>
                      <td className="px-4 py-3 text-xs">{p.type === "flat" ? "Flat" : `$${(p.rate_usd_per_ft || 0).toFixed(2)}/ft`}</td>
                      <td className="px-4 py-3">{p.amount_paid_usd ? money(p.amount_paid_usd) : p.amount_due_usd ? `${money(p.amount_due_usd)} due` : "—"}</td>
                      <td className="px-4 py-3">
                        <span className={`text-[10px] font-black uppercase px-2 py-1 rounded ${
                          p.status === "paid" ? "bg-emerald-500/15 text-emerald-400"
                          : p.status === "invoiced" ? "bg-amber-500/15 text-amber-400"
                          : p.status === "committed" ? "bg-cyan-500/15 text-cyan-400"
                          : "bg-white/10 text-white/50"}`}>{p.status}</span>
                      </td>
                      <td className="px-4 py-3">
                        {p.hosted_invoice_url ? <a href={p.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-xs text-cyan-400 hover:underline">Open</a> : "—"}
                      </td>
                    </tr>
                  ))}
                  {dash.pledges.length === 0 && (
                    <tr><td colSpan={6} className="px-4 py-8 text-center text-white/40">No pledges yet — share the campaign pages.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </>
      )}

      <ResultsDialog campaign={resultsFor} onClose={() => setResultsFor(null)} onSaved={() => { setResultsFor(null); loadDash(selected); }} />
      <TeamCheckoutDialog open={upgradeOpen} onOpenChange={setUpgradeOpen} packageId="team" />
      {dash && <EmailInviteDialog open={emailInviteOpen} onOpenChange={setEmailInviteOpen} eventId={dash.event.event_id} />}
    </div>
  );
}

function EmailInviteDialog({ open, onOpenChange, eventId }) {
  const [raw, setRaw] = useState("");
  const [busy, setBusy] = useState(false);
  const emails = raw.split(/[\s,;]+/).map((e) => e.trim()).filter((e) => /^[\w.\-+]+@[\w.\-]+\.[a-zA-Z]{2,}$/.test(e));
  const send = async () => {
    if (!emails.length) return toast.error("Paste at least one valid email");
    setBusy(true);
    try {
      const { data } = await api.post(`/fusion/events/${eventId}/invite-emails`, { emails, origin_url: window.location.origin });
      toast.success(`Invites sent to ${data.sent} athlete(s)${data.failed?.length ? ` — ${data.failed.length} failed` : ""}`);
      setRaw("");
      onOpenChange(false);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0d1830] border-white/10 text-white" data-testid="email-invite-dialog">
        <DialogHeader>
          <DialogTitle>Email the join link to your roster</DialogTitle>
          <DialogDescription className="text-white/60">
            Paste athlete emails (separated by commas, spaces, or new lines). Each gets a branded invite with your Fusion join link.
          </DialogDescription>
        </DialogHeader>
        <Textarea rows={6} value={raw} onChange={(e) => setRaw(e.target.value)}
          placeholder={"jordan@example.com\nmaya@example.com, chris@example.com"}
          className="bg-white/5 border-white/15 text-white" data-testid="invite-emails-input" />
        <div className="text-xs text-white/50">{emails.length} valid email(s) detected</div>
        <Button onClick={send} disabled={busy || emails.length === 0} className="bg-cyan-400 text-[#0b1424] font-bold" data-testid="invite-emails-send-btn">
          {busy ? "Sending…" : `Send ${emails.length} invite(s)`}
        </Button>
      </DialogContent>
    </Dialog>
  );
}

function CreateEventDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ name: "Fusion Combine & Hit-A-Thon", team_name: "", sport: "Baseball", event_date: "", location: "", team_goal_usd: 10000, athlete_default_goal_usd: 500, coach_message: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.name.trim()) return toast.error("Event name required");
    setBusy(true);
    try {
      const { data } = await api.post("/fusion/events", {
        ...f, team_goal_usd: Number(f.team_goal_usd), athlete_default_goal_usd: Number(f.athlete_default_goal_usd),
        event_date: f.event_date || null,
      });
      toast.success("Fusion event created");
      setOpen(false);
      onCreated(data.event);
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button onClick={() => setOpen(true)} size="sm" className="bg-cyan-400 text-[#0b1424] font-bold hover:bg-cyan-300" data-testid="create-event-btn">
        <Flame className="h-4 w-4 mr-1" /> New Fusion Event
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0d1830] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="create-event-dialog">
          <DialogHeader>
            <DialogTitle>Activate a Fusion Event</DialogTitle>
            <DialogDescription className="text-white/60">Team campaign page + athlete pages are auto-generated and auto-branded.</DialogDescription>
          </DialogHeader>
          <Input placeholder="Event name" value={f.name} onChange={set("name")} className="bg-white/5 border-white/15 text-white" data-testid="event-name-input" />
          <Input placeholder="Team name (e.g. Panthers Baseball)" value={f.team_name} onChange={set("team_name")} className="bg-white/5 border-white/15 text-white" data-testid="event-team-input" />
          <select value={f.sport} onChange={set("sport")} className="h-9 w-full rounded-md px-3 text-sm bg-white/5 border border-white/15 text-white" data-testid="event-sport-select">
            {["Baseball", "Softball", "Football", "Basketball", "Volleyball", "Soccer", "Track & Field", "Multi-sport"].map((s) => <option key={s} value={s} className="bg-[#0d1830]">{s}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <Input type="date" value={f.event_date} onChange={set("event_date")} className="bg-white/5 border-white/15 text-white" data-testid="event-date-input" />
            <Input placeholder="Location" value={f.location} onChange={set("location")} className="bg-white/5 border-white/15 text-white" data-testid="event-location-input" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40">Team goal ($)</label>
              <Input type="number" value={f.team_goal_usd} onChange={set("team_goal_usd")} className="bg-white/5 border-white/15 text-white" data-testid="event-team-goal-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40">Athlete default goal ($)</label>
              <Input type="number" value={f.athlete_default_goal_usd} onChange={set("athlete_default_goal_usd")} className="bg-white/5 border-white/15 text-white" data-testid="event-athlete-goal-input" />
            </div>
          </div>
          <Textarea placeholder="Coach message (shows on the team campaign page)" value={f.coach_message} onChange={set("coach_message")} className="bg-white/5 border-white/15 text-white" data-testid="event-coach-message-input" />
          <Button onClick={submit} disabled={busy} className="bg-cyan-400 text-[#0b1424] font-bold" data-testid="event-create-submit-btn">
            {busy ? "Creating…" : "Create event"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function EmbedDialog({ slug }) {
  const [open, setOpen] = useState(false);
  const origin = window.location.origin;
  const iframe = `<iframe src="${origin}/fusion/embed/${slug}" title="Hit-A-Thon progress" width="100%" height="300" style="border:0;border-radius:16px;max-width:520px" loading="lazy"></iframe>`;
  const script = `<div data-biohacker-fusion="${slug}"></div>\n<script src="${origin}/api/fusion/public/team/${slug}/widget.js" async></script>`;
  const json = `${origin}/api/fusion/public/team/${slug}/widget.json`;
  const copy = (txt, label) => navigator.clipboard.writeText(txt).then(() => toast.success(`${label} copied`));
  const Block = ({ title, hint, code, testId }) => (
    <div className="rounded-xl border border-white/10 bg-white/[0.03] p-3">
      <div className="flex items-center justify-between gap-2">
        <div><div className="text-xs font-bold text-white">{title}</div><div className="text-[11px] text-white/50">{hint}</div></div>
        <Button size="sm" variant="outline" className="border-white/20 text-white shrink-0" onClick={() => copy(code, title)} data-testid={`${testId}-copy-btn`}>
          <Copy className="h-3.5 w-3.5 mr-1" /> Copy
        </Button>
      </div>
      <pre className="mt-2 text-[11px] text-cyan-200/90 whitespace-pre-wrap break-all font-mono" data-testid={`${testId}-code`}>{code}</pre>
    </div>
  );
  return (
    <>
      <Button onClick={() => setOpen(true)} variant="outline" className="border-white/20 text-white" data-testid="embed-widget-btn">
        <Code2 className="h-4 w-4 mr-1.5" /> Embed on your website
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0d1830] border-white/10 text-white max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="embed-widget-dialog">
          <DialogHeader>
            <DialogTitle>Show the amount raised on your own website</DialogTitle>
            <DialogDescription className="text-white/60">
              Paste one snippet into your team site (Wix, Squarespace, GoDaddy, WordPress, custom HTML). It updates automatically as pledges come in and links back to your campaign page.
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl overflow-hidden border border-white/10">
            <iframe src={`${origin}/fusion/embed/${slug}`} title="Widget preview" width="100%" height="300" style={{ border: 0 }} data-testid="embed-preview-iframe" />
          </div>
          <Block title="Option 1 — Iframe (recommended)" hint="Works on every site builder's 'Embed HTML / Custom code' block. Add ?theme=light to the src for a white card." code={iframe} testId="embed-iframe" />
          <Block title="Option 2 — Script widget" hint={'Renders inline with your page styles; no iframe border. Add data-theme="light" to the div for a white card.'} code={script} testId="embed-script" />
          <Block title="Option 3 — Live JSON feed" hint="For developers: raised_usd, goal_usd, pct_to_goal, athlete_count, sponsor_count. CORS-open, refreshes every 60s." code={json} testId="embed-json" />
        </DialogContent>
      </Dialog>
    </>
  );
}

function EditEventDialog({ event, onSaved }) {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setF({
      name: event.name || "", team_name: event.team_name || "", sport: event.sport || "Baseball",
      event_date: event.event_date || "", location: event.location || "",
      team_goal_usd: event.team_goal_usd ?? 10000, athlete_default_goal_usd: event.athlete_default_goal_usd ?? 500,
      coach_message: event.coach_message || "", status: event.status || "active",
    });
  }, [open, event]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.name.trim()) return toast.error("Event name required");
    setBusy(true);
    try {
      await api.patch(`/fusion/events/${event.event_id}`, {
        ...f, team_goal_usd: Number(f.team_goal_usd), athlete_default_goal_usd: Number(f.athlete_default_goal_usd),
        event_date: f.event_date || null,
      });
      toast.success("Event updated");
      setOpen(false);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Button onClick={() => setOpen(true)} variant="outline" className="border-white/20 text-white" data-testid="edit-event-btn">
        <Pencil className="h-4 w-4 mr-1.5" /> Edit event
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0d1830] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="edit-event-dialog">
          <DialogHeader>
            <DialogTitle>Edit Fusion Event</DialogTitle>
            <DialogDescription className="text-white/60">Change the date, goals, or details. Public team and athlete pages update instantly.</DialogDescription>
          </DialogHeader>
          <Input placeholder="Event name" value={f.name || ""} onChange={set("name")} className="bg-white/5 border-white/15 text-white" data-testid="edit-event-name-input" />
          <Input placeholder="Team name" value={f.team_name || ""} onChange={set("team_name")} className="bg-white/5 border-white/15 text-white" data-testid="edit-event-team-input" />
          <select value={f.sport || "Baseball"} onChange={set("sport")} className="h-9 w-full rounded-md px-3 text-sm bg-white/5 border border-white/15 text-white" data-testid="edit-event-sport-select">
            {["Baseball", "Softball", "Football", "Basketball", "Volleyball", "Soccer", "Track & Field", "Multi-sport"].map((s) => <option key={s} value={s} className="bg-[#0d1830]">{s}</option>)}
          </select>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40">Event date</label>
              <Input type="date" value={f.event_date || ""} onChange={set("event_date")} className="bg-white/5 border-white/15 text-white" data-testid="edit-event-date-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40">Location</label>
              <Input placeholder="Location" value={f.location || ""} onChange={set("location")} className="bg-white/5 border-white/15 text-white" data-testid="edit-event-location-input" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40">Team goal ($)</label>
              <Input type="number" value={f.team_goal_usd ?? ""} onChange={set("team_goal_usd")} className="bg-white/5 border-white/15 text-white" data-testid="edit-event-team-goal-input" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-white/40">Athlete default goal ($)</label>
              <Input type="number" value={f.athlete_default_goal_usd ?? ""} onChange={set("athlete_default_goal_usd")} className="bg-white/5 border-white/15 text-white" data-testid="edit-event-athlete-goal-input" />
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">Status</label>
            <select value={f.status || "active"} onChange={set("status")} className="h-9 w-full rounded-md px-3 text-sm bg-white/5 border border-white/15 text-white" data-testid="edit-event-status-select">
              {["draft", "active", "completed", "archived"].map((s) => <option key={s} value={s} className="bg-[#0d1830]">{s}</option>)}
            </select>
          </div>
          <Textarea placeholder="Coach message (shows on the team campaign page)" value={f.coach_message || ""} onChange={set("coach_message")} className="bg-white/5 border-white/15 text-white" data-testid="edit-event-coach-message-input" />
          <Button onClick={submit} disabled={busy} className="bg-cyan-400 text-[#0b1424] font-bold" data-testid="edit-event-save-btn">
            {busy ? "Saving…" : "Save changes"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function BrandingDialog({ event, onSaved }) {
  const [open, setOpen] = useState(false);
  const b = event.branding || {};
  const [f, setF] = useState({
    school_name: b.school_name || "", logo_url: b.logo_url || "",
    team_photo_url: b.team_photo_url || "",
    primary_color: b.primary_color || "#0B1E36", secondary_color: b.secondary_color || "#00E0C6",
    accent_color: b.accent_color || "#FFB703",
  });
  const [flyerName, setFlyerName] = useState(event.flyer_name || "");
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/fusion/events/${event.event_id}`, { branding: f });
      toast.success("School branding saved — all athlete pages updated automatically");
      setOpen(false); onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const readFile = (file, maxMB, cb) => {
    if (file.size > maxMB * 1024 * 1024) return toast.error(`File must be under ${maxMB} MB`);
    const r = new FileReader();
    r.onload = () => cb(r.result);
    r.readAsDataURL(file);
  };
  const onLogoFile = (e) => { const file = e.target.files?.[0]; if (file) readFile(file, 1.5, (d) => setF((p) => ({ ...p, logo_url: d }))); };
  const onPhotoFile = (e) => { const file = e.target.files?.[0]; if (file) readFile(file, 2.5, (d) => setF((p) => ({ ...p, team_photo_url: d }))); };
  const onFlyerFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    readFile(file, 8, async (d) => {
      try {
        await api.post(`/fusion/events/${event.event_id}/flyer`, { file_b64: d, name: file.name });
        setFlyerName(file.name);
        toast.success("Flyer attached to your team campaign page");
        onSaved();
      } catch (er) { toast.error(formatApiError(er)); }
    });
  };
  return (
    <>
      <Button onClick={() => setOpen(true)} variant="outline" className="border-white/20 text-white" data-testid="branding-btn">
        <Palette className="h-4 w-4 mr-1.5" /> Branding & media
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#0d1830] border-white/10 text-white max-h-[90vh] overflow-y-auto" data-testid="branding-dialog">
          <DialogHeader>
            <DialogTitle>School Branding & Media</DialogTitle>
            <DialogDescription className="text-white/60">
              Propagates automatically to the team campaign page and every athlete fundraiser page.
            </DialogDescription>
          </DialogHeader>
          <Input placeholder="School name" value={f.school_name} onChange={set("school_name")} className="bg-white/5 border-white/15 text-white" data-testid="branding-school-input" />
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">School logo</label>
            <input type="file" accept="image/*" onChange={onLogoFile} className="block w-full text-xs text-white/60 mt-1" data-testid="branding-logo-input" />
            {f.logo_url && <img src={f.logo_url} alt="logo" className="h-12 mt-2 rounded bg-white/90 p-1" />}
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">Team landing page photo (hero banner)</label>
            <input type="file" accept="image/*" onChange={onPhotoFile} className="block w-full text-xs text-white/60 mt-1" data-testid="branding-team-photo-input" />
            {f.team_photo_url && <img src={f.team_photo_url} alt="team" className="h-24 w-full object-cover mt-2 rounded-lg" data-testid="branding-team-photo-preview" />}
            <div className="text-[10px] text-white/35 mt-1">Shown as the big banner at the top of your public team campaign page. Landscape photos work best (max 2.5 MB).</div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-white/40">Fundraiser flyer (PDF or image)</label>
            <input type="file" accept="application/pdf,image/*" onChange={onFlyerFile} className="block w-full text-xs text-white/60 mt-1" data-testid="branding-flyer-input" />
            {flyerName && (
              <div className="mt-2 flex items-center gap-2 text-xs text-white/70" data-testid="branding-flyer-name">
                <FileText className="h-4 w-4 text-cyan-400" /> {flyerName}
                <a href={`${API}/fusion/flyer/${event.event_id}`} target="_blank" rel="noreferrer" className="text-cyan-400 underline">View</a>
              </div>
            )}
            <div className="text-[10px] text-white/35 mt-1">Attached to your public team page with a "View flyer" button — flyers upload instantly (max 8 MB).</div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[["primary_color", "Primary"], ["secondary_color", "Secondary"], ["accent_color", "Accent"]].map(([k, l]) => (
              <div key={k}>
                <label className="text-[10px] uppercase tracking-widest text-white/40">{l}</label>
                <input type="color" value={f[k]} onChange={set(k)} className="block w-full h-10 rounded cursor-pointer bg-transparent" data-testid={`branding-${k}-input`} />
              </div>
            ))}
          </div>
          <Button onClick={save} disabled={busy} className="bg-cyan-400 text-[#0b1424] font-bold" data-testid="branding-save-btn">
            {busy ? "Saving…" : "Save & propagate to all athlete pages"}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

function ResultsDialog({ campaign, onClose, onSaved }) {
  const [swings, setSwings] = useState(["", "", "", "", ""]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (campaign) setSwings((campaign.swings_ft || ["", "", "", "", ""]).map(String));
  }, [campaign]);
  if (!campaign) return null;
  const best = Math.max(...swings.map((s) => Number(s) || 0));
  const submit = async () => {
    const vals = swings.map(Number);
    if (vals.some((v) => !v || v <= 0)) return toast.error("Enter all five swing distances (feet)");
    setBusy(true);
    try {
      const { data } = await api.post(`/fusion/campaigns/${campaign.campaign_id}/results`, { swings_ft: vals, verified: true });
      toast.success(`Verified! Best: ${data.best_distance_ft} ft · ${data.invoiced} invoice(s) sent`);
      if (data.failures?.length) toast.error(`${data.failures.length} invoice(s) failed — check Stripe`);
      onSaved();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!campaign} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-[#0d1830] border-white/10 text-white" data-testid="results-dialog">
        <DialogHeader>
          <DialogTitle><Trophy className="inline h-5 w-5 mr-1 text-amber-400" /> Five-Swing Result — {campaign.athlete_name}</DialogTitle>
          <DialogDescription className="text-white/60">
            Verifying finalizes per-foot pledges: Stripe invoices are emailed to sponsors' billing addresses, and the best distance feeds the athlete's permanent BioHacker profile.
          </DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-5 gap-2">
          {swings.map((s, i) => (
            <div key={i}>
              <label className="text-[10px] uppercase tracking-widest text-white/40">Swing {i + 1}</label>
              <Input type="number" min="1" value={s} placeholder="ft"
                onChange={(e) => setSwings(swings.map((x, j) => (j === i ? e.target.value : x)))}
                className="bg-white/5 border-white/15 text-white" data-testid={`swing-input-${i + 1}`} />
            </div>
          ))}
        </div>
        {best > 0 && <div className="text-sm text-white/70">Best distance: <b className="text-amber-400">{best} ft</b></div>}
        <Button onClick={submit} disabled={busy} className="bg-amber-400 text-[#0b1424] font-bold" data-testid="results-verify-btn">
          {busy ? "Verifying & invoicing…" : "Verify result & send invoices"}
        </Button>
      </DialogContent>
    </Dialog>
  );
}
